import React, { useRef } from "react";
import { AnimatePresence, motion, PanInfo } from "framer-motion";
import { NavButton } from "./NavButton";

interface CardShellProps {
  currentIndex: number;
  total: number;
  topic?: string;
  onNext?: () => void;
  onPrev?: () => void;
  nextDisabled?: boolean;
  nextLabel?: string;
  prevLabel?: string;
  isRTL?: boolean;
  sceneKey?: string | number;
  progress?: React.ReactNode;
  toolbar?: React.ReactNode;
  allowNextOnLast?: boolean;
  allowPrevOnFirst?: boolean;
  children: React.ReactNode;
}

const cardVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 88 : -88,
    opacity: 0,
    scale: 0.98,
    y: 8,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    y: 0,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -56 : 56,
    opacity: 0,
    scale: 0.99,
    y: 6,
  }),
};

const cardTransition = {
  type: "spring",
  stiffness: 380,
  damping: 32,
  mass: 0.8,
};

export function CardShell({
  currentIndex,
  total,
  topic,
  onNext,
  onPrev,
  nextDisabled = false,
  nextLabel,
  prevLabel,
  isRTL = false,
  sceneKey,
  progress,
  toolbar,
  allowNextOnLast = false,
  allowPrevOnFirst = false,
  children,
}: CardShellProps) {
  const safeTotal = Math.max(total, 1);
  const displayIndex = Math.min(Math.max(currentIndex, 0), safeTotal - 1);
  const isFirst = displayIndex <= 0;
  const isLast = displayIndex >= safeTotal - 1;
  const showNext = Boolean(onNext) && (!isLast || allowNextOnLast);
  const showPrev = Boolean(onPrev) && (!isFirst || allowPrevOnFirst);

  const prevIndexRef = useRef(displayIndex);
  const direction = displayIndex >= prevIndexRef.current ? 1 : -1;
  prevIndexRef.current = displayIndex;

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = 72;
    if (info.offset.x < -threshold && showNext && !nextDisabled) {
      onNext?.();
      return;
    }
    if (info.offset.x > threshold && showPrev) {
      onPrev?.();
    }
  };

  return (
    <div
      className="relative flex h-full w-full flex-1 items-center justify-center px-3 py-2 sm:px-5 sm:py-3"
      data-testid="card-shell"
      data-presentation="cards"
    >
      <div className="flex w-full max-w-5xl items-center justify-center gap-3 sm:gap-5">
        <div className="hidden h-14 w-14 shrink-0 sm:flex sm:h-16 sm:w-16 items-center justify-center">
          {showPrev ? (
            <NavButton
              direction="prev"
              onClick={onPrev!}
              label={prevLabel || "Previous card"}
              isRTL={isRTL}
              dataTestId="card-shell-prev"
            />
          ) : null}
        </div>

        <div className="relative w-full max-w-3xl lg:max-w-4xl">
          <div
            aria-hidden="true"
            data-testid="card-shell-silhouette"
            className="glass-border-2 pointer-events-none absolute inset-x-4 top-3 -bottom-2 opacity-50"
          />

          <AnimatePresence mode="wait" initial={false} custom={direction}>
            <motion.article
              key={sceneKey ?? displayIndex}
              custom={direction}
              variants={cardVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={cardTransition}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.18}
              onDragEnd={handleDragEnd}
              className="glass-border-2 relative z-10 flex h-[calc(100vh-1.75rem)] min-h-[28rem] max-h-[calc(100vh-1.75rem)] flex-col overflow-visible px-8 py-7 sm:px-10 sm:py-8"
            >
              <div className="corner-top-left pointer-events-none" />
              <div className="corner-bottom-right pointer-events-none" />

              <div className="relative z-10 mb-5 shrink-0 overflow-visible">
                <div className="mb-3 flex w-full min-w-0 items-start justify-between gap-4">
                  {topic ? (
                    <h2
                      data-testid="card-shell-topic"
                      className="min-w-0 flex-1 text-left text-base font-semibold leading-snug tracking-tight text-[#1C1C1E] sm:text-lg dark:text-[#F2F2F7]"
                    >
                      {topic}
                    </h2>
                  ) : (
                    <span className="flex-1" />
                  )}
                  <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                    {toolbar ? (
                      <div className="flex items-center gap-1.5 sm:gap-2" data-testid="card-shell-toolbar">
                        {toolbar}
                      </div>
                    ) : null}
                    <span
                      data-testid="card-shell-counter"
                      className="text-sm font-semibold tabular-nums text-[#1C1C1E] dark:text-[#F2F2F7]"
                    >
                      {displayIndex + 1} / {safeTotal}
                    </span>
                  </div>
                </div>
                {progress ? (
                  <div data-testid="card-shell-progress">
                    {progress}
                  </div>
                ) : null}
              </div>

              <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-start overflow-y-auto" data-testid="card-shell-body">
                {children}
              </div>
            </motion.article>
          </AnimatePresence>
        </div>

        <div className="hidden h-14 w-14 shrink-0 sm:flex sm:h-16 sm:w-16 items-center justify-center">
          {showNext ? (
            <NavButton
              direction="next"
              onClick={onNext!}
              disabled={nextDisabled}
              label={nextLabel || "Next card"}
              isRTL={isRTL}
              dataTestId="card-shell-next"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
